import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type ButtonVariant = 'primary' | 'ink' | 'secondary' | 'ghost' | 'inverted';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Rendered after the label, as in the DS Button (`children, icon`). */
  readonly icon?: ReactNode;
  /** Rendered before the label. */
  readonly leadingIcon?: ReactNode;
  /** Lets the label wrap onto several lines; the height becomes a minimum. */
  readonly wrap?: boolean;
}

/*
 * Pingine DS Button (_ds_bundle Button.jsx): weight 600, radius 10 (8 for sm), 1px transparent
 * border, 120ms ease-out, inline-flex, gap 8, nowrap. Heights are pinned to the screens'
 * declared sizes (sm 32px, md 40px). States follow the DS README (the bundle has none): coral and
 * navy step to their -hover / -press tokens, press moves 1px down, disabled is 40% opacity;
 * secondary and ghost use the screens' own hover conventions (border-2, neutral-100).
 * Inside navy bulk bars pass className="h-[30px]". `wrap` is for a narrow cell: the label may
 * break onto two lines and the size's height turns into a minimum.
 */
const BASE =
  'inline-flex items-center justify-center gap-2 border font-sans font-semibold ' +
  'transition-all duration-[120ms] ease-out enabled:active:translate-y-px ' +
  'disabled:cursor-not-allowed disabled:opacity-40';

const SIZES: Record<ButtonSize, string> = {
  sm: 'rounded-lg px-3 text-[13px] leading-[18px]',
  md: 'rounded-2 px-[18px] text-[15px] leading-[20px]',
  lg: 'rounded-2 px-[22px] text-[16px] leading-[22px]',
};

const HEIGHTS: Record<ButtonSize, { readonly fixed: string; readonly wrap: string }> = {
  sm: { fixed: 'h-8', wrap: 'min-h-8 py-1' },
  md: { fixed: 'h-10', wrap: 'min-h-10 py-1.5' },
  lg: { fixed: 'h-[52px]', wrap: 'min-h-[52px] py-2' },
};

const VARIANTS: Record<ButtonVariant, string> = {
  // Border colour is set per variant: a shared border-transparent would override secondary's border.
  primary: 'border-transparent bg-pi-coral text-pi-navy enabled:hover:bg-pi-coral-hover enabled:active:bg-pi-coral-press',
  ink: 'border-transparent bg-pi-navy text-pi-paper enabled:hover:bg-pi-navy-hover enabled:active:bg-pi-navy-press',
  secondary: 'border-border-1 bg-bg-3 text-pi-navy enabled:hover:border-border-2 enabled:active:bg-bg-1',
  ghost: 'border-transparent bg-transparent text-pi-navy enabled:hover:bg-neutral-100 enabled:active:bg-neutral-200',
  inverted: 'border-transparent bg-pi-paper text-pi-navy enabled:hover:bg-neutral-100 enabled:active:bg-neutral-200',
};

export function buttonClassName(variant: ButtonVariant = 'primary', size: ButtonSize = 'md', wrap = false): string {
  const height = wrap ? cn(HEIGHTS[size].wrap, 'whitespace-normal') : cn(HEIGHTS[size].fixed, 'whitespace-nowrap');
  return cn(BASE, height, SIZES[size], VARIANTS[variant]);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', wrap = false, icon, leadingIcon, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      data-variant={variant}
      data-size={size}
      className={cn(buttonClassName(variant, size, wrap), className)}
      {...rest}
    >
      {leadingIcon}
      {children}
      {icon}
    </button>
  );
});
